import { createNodeRuleSet, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadShape, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, pinMarkerMargin, pinMarkerRadius, silkWidth } from "./commonConstants";
/**
 * Chip component package dimensions in meters
 * Format: [length, width, height, padLength, padWidth, padSpacing]
 * All values are in millimeters and will be converted to meters
 */
export const chipPackageDimensions = {
    "0201": { length: 0.6, width: 0.3, height: 0.3, padLength: 0.3, padWidth: 0.3, padSpacing: 0.3 },
    "0402": { length: 1.0, width: 0.5, height: 0.35, padLength: 0.4, padWidth: 0.5, padSpacing: 0.5 },
    "0603": { length: 1.6, width: 0.8, height: 0.45, padLength: 0.5, padWidth: 0.8, padSpacing: 0.8 },
    "0805": { length: 2.0, width: 1.25, height: 0.5, padLength: 0.6, padWidth: 1.25, padSpacing: 1.0 },
    "1206": { length: 3.2, width: 1.6, height: 0.55, padLength: 0.8, padWidth: 1.6, padSpacing: 1.8 },
    "1210": { length: 3.2, width: 2.5, height: 0.55, padLength: 0.8, padWidth: 2.5, padSpacing: 1.8 },
    "1806": { length: 4.5, width: 1.6, height: 0.55, padLength: 1.0, padWidth: 1.6, padSpacing: 2.7 },
    "1812": { length: 4.5, width: 3.2, height: 0.55, padLength: 1.0, padWidth: 3.2, padSpacing: 2.7 },
    "2010": { length: 5.0, width: 2.5, height: 0.55, padLength: 1.0, padWidth: 2.5, padSpacing: 3.2 },
    "2512": { length: 6.3, width: 3.2, height: 0.55, padLength: 1.2, padWidth: 3.2, padSpacing: 4.1 },
    "1825": { length: 4.5, width: 6.4, height: 0.55, padLength: 1.0, padWidth: 6.4, padSpacing: 2.7 },
};
/**
 * Get the side for a pin in chip packages (which have pins on two sides only)
 * Side 0 = left side (pad 1), Side 1 = right side (pad 2)
 */
function getSide(i) {
    if (i < 1) {
        return 0;
    }
    else if (i < 2) {
        return 1;
    }
    return -1;
}
function chipInferPinLayout(bakedRules, existingPadNodes) {
    const result = { grid: [], otherPads: [] };
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => pad.terminalId === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const remainingPadsWithNumbers = existingPadNodes.filter((pad) => pad.terminalId !== "" && pad.terminalId !== undefined);
    const total = 2; // Chip components always have 2 pads
    for (let i = 0; i < total; i++) {
        const foundMatchingI = remainingPadsWithNumbers.findIndex((pad) => pad.terminalId === `${i + 1}`);
        const foundMatching = remainingPadsWithNumbers[foundMatchingI];
        if (foundMatchingI !== -1) {
            remainingPadsWithNumbers.splice(foundMatchingI, 1);
        }
        const matchingPad = foundMatching ?? withoutPinNumberPadNodes.shift();
        const sideId = getSide(i);
        if (matchingPad && sideId === -1) {
            result.otherPads.push(matchingPad);
        }
        else {
            const sideArr = result.grid[sideId] ?? [];
            sideArr.push(matchingPad ?? null);
            result.grid[sideId] = sideArr;
        }
    }
    result.otherPads.push(...withoutPinNumberPadNodes);
    result.otherPads.push(...remainingPadsWithNumbers);
    return result;
}
export function chipComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSizeX, y: config.pinSizeY });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.padSpacing, y: 0 });
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: 1, // 1 pad per side
        y: 0, // Chip components are two-sided
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        x: config.packageLength,
        y: config.packageWidth,
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
    };
    return { ...newBakedRules, pinLayout: chipInferPinLayout(newBakedRules, existingPadNodes) };
}
export function chipGeneratePadRules(footprintNodeUid, bakedRules) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const padSpacing = bakedRules.pinPitch?.x ?? 0;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result;
    for (let sideId = 0; sideId < pinLayout.grid.length; sideId++) {
        const sideArr = pinLayout.grid[sideId];
        if (!sideArr)
            continue;
        for (const matchingPad of sideArr) {
            if (!matchingPad)
                continue;
            let posX = 0;
            const posY = 0;
            if (sideId === 0) {
                // Left pad (pad 1)
                posX = -padSpacing / 2;
            }
            else if (sideId === 1) {
                // Right pad (pad 2)
                posX = padSpacing / 2;
            }
            const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
            const ruleSet = {
                uid: matchingPad.uid,
                selector: selector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            result[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                size: `${metersToMm(pinSizeX)}mm ${metersToMm(pinSizeY)}mm`,
                padShape: FootPrintPadShape.rectangle,
            };
            ruleSet.rules = createNodeRuleSet(rules);
        }
    }
    return result;
}
export function chipGenerateAdditionalPads(_footprintNodeUid, _bakedRules) {
    // Chip components don't have additional pads
    return [];
}
export function chipGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    const packageLength = bakedRules.packageSize.x;
    const packageWidth = bakedRules.packageSize.y;
    // Outer shell - simple rectangle for chip components
    silkPaths.push(`<path d="
              M ${-packageLength / 2} ${-packageWidth / 2}
              L ${packageLength / 2} ${-packageWidth / 2}
              L ${packageLength / 2} ${packageWidth / 2}
              L ${-packageLength / 2} ${packageWidth / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    // Pin 1 marker (small circle on the left side)
    silkPaths.push(`<circle
              cx="${-packageLength / 2 - pinMarkerMargin}"
              cy="${0}"
              r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    // Designator text
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(packageWidth / 2 + designatorMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
/**
 * Creates a chip component generator config from a package size code
 * @param sizeCode The size code (e.g., "0402", "0603", "0805")
 * @returns Configuration object for chipComputeInferredRules
 */
export function getChipConfig(sizeCode) {
    const dims = chipPackageDimensions[sizeCode];
    if (!dims) {
        // Default to 0402 if unknown size
        const defaultDims = chipPackageDimensions["0402"];
        return {
            packageHeight: (defaultDims?.height ?? 0.35) / 1000,
            pinSizeX: (defaultDims?.padLength ?? 0.4) / 1000,
            pinSizeY: (defaultDims?.padWidth ?? 0.5) / 1000,
            padSpacing: (defaultDims?.padSpacing ?? 0.5) / 1000,
            packageLength: (defaultDims?.length ?? 1.0) / 1000,
            packageWidth: (defaultDims?.width ?? 0.5) / 1000,
        };
    }
    return {
        packageHeight: dims.height / 1000,
        pinSizeX: dims.padLength / 1000,
        pinSizeY: dims.padWidth / 1000,
        padSpacing: dims.padSpacing / 1000,
        packageLength: dims.length / 1000,
        packageWidth: dims.width / 1000,
    };
}
//# sourceMappingURL=chip.js.map